// src/components/AccountStatusGate.jsx
//
// The full-screen state for an account that is scheduled for deletion or has been
// suspended.
//
// This screen is the entire reason the 30-day grace period is meaningful: the
// backend locks a pending-deletion account out of every route EXCEPT the handful
// that let it look at itself, export its data, and change its mind. If the user
// were simply dropped back to the login form, "restorable for 30 days" would be a
// claim with no mechanism behind it.
import React, { useState } from 'react';
import '../css/QdexAuth.css';

const Shell = ({ subtitle, children }) => (
    <div className="qdex-auth-wrapper">
        <div className="qdex-auth-card">
            <div className="qdex-brand">
                <h1 className="qdex-logo">qdex</h1>
                <p className="qdex-subtitle">{subtitle}</p>
            </div>
            {children}
        </div>
    </div>
);

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
    });
}

/**
 * @param status              'pending_deletion' | 'suspended'
 * @param deletionScheduledAt ISO date the purge becomes possible
 * @param onRestore           cancels the scheduled deletion
 * @param onExport            downloads the account's data
 * @param onSignOut
 */
function AccountStatusGate({ status, deletionScheduledAt, onRestore, onExport, onSignOut }) {
    const [busy, setBusy] = useState(null); // 'restore' | 'export' | null
    const [error, setError] = useState('');

    const run = async (kind, fn) => {
        setBusy(kind);
        setError('');
        try {
            await fn();
        } catch (err) {
            console.error(`Account gate action "${kind}" failed:`, err);
            setError(
                err?.response?.data?.message ||
                'That didn\'t work. Please try again in a moment.',
            );
        } finally {
            setBusy(null);
        }
    };

    if (status === 'suspended') {
        return (
            <Shell subtitle="Account suspended">
                <h2>This account has been suspended</h2>
                <p className="qdex-lead">
                    Access has been disabled by an administrator. If you think this is a
                    mistake, reply to the email you received or contact support.
                </p>
                {error && <div className="qdex-error-message">{error}</div>}
                <div className="qdex-actions">
                    {/* Export stays available: suspension shouldn't strand someone
                        without their own data. */}
                    <button
                        className="qdex-btn qdex-btn-google"
                        onClick={() => run('export', onExport)}
                        disabled={busy !== null}
                    >
                        {busy === 'export' ? 'Preparing…' : 'Download my data'}
                    </button>
                    <button className="qdex-btn qdex-btn-google" onClick={onSignOut} disabled={busy !== null}>
                        Sign out
                    </button>
                </div>
            </Shell>
        );
    }

    const when = formatDate(deletionScheduledAt);

    return (
        <Shell subtitle="Account scheduled for deletion">
            <h2>Your account is scheduled for deletion</h2>
            <p className="qdex-lead">
                {when
                    ? <>Everything will be permanently deleted on <strong>{when}</strong>. Nothing has been removed yet.</>
                    : <>Everything will be permanently deleted after the grace period. Nothing has been removed yet.</>}
            </p>

            {error && <div className="qdex-error-message">{error}</div>}

            <div className="qdex-actions">
                <button
                    className="qdex-btn qdex-btn-primary"
                    onClick={() => run('restore', onRestore)}
                    disabled={busy !== null}
                >
                    {busy === 'restore' ? 'Restoring…' : 'Restore my account'}
                </button>
                <button
                    className="qdex-btn qdex-btn-google"
                    onClick={() => run('export', onExport)}
                    disabled={busy !== null}
                >
                    {busy === 'export' ? 'Preparing…' : 'Download my data'}
                </button>
                <button className="qdex-btn qdex-btn-google" onClick={onSignOut} disabled={busy !== null}>
                    Sign out
                </button>
            </div>

            <p className="qdex-fineprint">
                Your API tokens were revoked when deletion was requested. Restoring the
                account does not bring them back — create new ones from Settings.
            </p>
        </Shell>
    );
}

export default AccountStatusGate;
